import { useCallback, useMemo, useSyncExternalStore } from "react";
import { guestsSubscription } from "@/lib/firestore/subscriptions/guests";
import { NO_WEDDING_KEY } from "@/lib/firestore/subscription-keys";
import {
  EMPTY_GUESTS,
  GUESTS_SNAPSHOT_EMPTY,
  GUESTS_SNAPSHOT_LOADING,
} from "@/lib/firestore/subscription-snapshots";
import { useWedding } from "./useWedding";
import type { Guest } from "@amazing-wedding/shared-types";

interface UseGuestsResult {
  guests: Guest[];
  loading: boolean;
}

/**
 * Subscribes to /weddings/{wid}/guests for the current wedding.
 * Soft-deleted guests (deletedAt !== null) are filtered OUT by default — pass
 * `includeDeleted: true` if you ever need the Trash view (not in v0.2).
 *
 * Returned guests are ordered by createdAt ASC; consumers can re-sort.
 *
 * Backed by a refcounted shared Firestore subscription keyed by weddingId.
 */
export function useGuests({
  includeDeleted = false,
}: { includeDeleted?: boolean } = {}): UseGuestsResult {
  const { wedding, loading: weddingLoading } = useWedding();
  const key = wedding?.id ?? NO_WEDDING_KEY;

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!wedding?.id || weddingLoading) return () => {};
      return guestsSubscription.subscribe(key, listener);
    },
    [wedding?.id, weddingLoading, key],
  );

  const getSnapshot = useCallback(() => {
    if (weddingLoading) return GUESTS_SNAPSHOT_LOADING;
    if (!wedding?.id) return GUESTS_SNAPSHOT_EMPTY;
    return guestsSubscription.getSnapshot(key);
  }, [wedding?.id, weddingLoading, key]);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => GUESTS_SNAPSHOT_LOADING);

  const guests = useMemo(() => {
    if (!wedding?.id) return EMPTY_GUESTS;
    if (includeDeleted) return snapshot.data;
    return snapshot.data.filter((g) => !g.deletedAt);
  }, [wedding?.id, includeDeleted, snapshot.data]);

  if (weddingLoading) {
    return { guests: EMPTY_GUESTS, loading: true };
  }
  if (!wedding) {
    return { guests: EMPTY_GUESTS, loading: false };
  }

  return { guests, loading: snapshot.loading };
}
